import { faMarker } from '@fortawesome/pro-duotone-svg-icons/faMarker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { withStyles, WithStyles } from "@mui/styles";
import * as pubApi from "api/Knowledge";
import styles from "assets/jss/cardCreation.styles";
import classNames from "classnames";
import IconButtonClose from 'components/Buttons/IconButtonClose';
import IconButtonExpandScreen from 'components/Buttons/IconButtonExpandScreen';
import { RequestIndicator } from 'components/CallServer';
import RequestErrorDialog from 'components/CallServer/RequestErrorDialog';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import { ConvertUploadToFileThumbnaildGetDto, UploadingStatus } from 'components/FileUpload/AttachSelection';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiWell from 'components/MuiExtras/MuiWell';
import QuillEditor from 'components/Quill/QuillEditor';
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import HtmlUtils from 'lib/HtmlUtils';
import MediaUtils from 'lib/MediaUtils';
import { SystemRoles } from 'models/ApiEnums';
import { AsyncUploadModel, ContentMetadata, FileThumbnaildGetDto, GetAnswerDto, SaveAnswerDto, SavePublicationResult } from 'models/ApiModels';
import { AttachType, RequestLifeCycle } from 'models/AppModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import Session from 'services/Session';
import { searchLink } from 'views/KnowledgeTest/Components/HandleAttach';
import HandleAttach from 'views/Publication/Components/HandleAttach';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IValidation {
    description?: boolean;
}

interface IProps {
    answerId?: string;
    questionId?: string;
    questionTitle?: string;
    onClose: (result: SavePublicationResult) => void;
}

interface IState {
    record: Partial<GetAnswerDto>;
    validation: IValidation;
    expanded: boolean;
    formCycle: RequestLifeCycle;
    lastError: any;
    maxHeight: number;

    richEditor: boolean;
    forceRichEditor: boolean,

    selectedAttach?: AttachType;
    uploadingStatus: UploadingStatus;
}


interface IDispatchProps {
}

type Props = IProps & IDispatchProps & WithStyles<typeof styles> & WithSnackbar & WithTranslation;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class CreateAnswer extends React.Component<Props, IState> {
    private qlEditorId = `ql${new Date().getTime()}`;
    private minHeight = 200;
    private editorRef: HTMLElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            record: {
                publicationId: props.answerId,
                questionId: props.questionId,
                contentBody: "",
                title: "",
                topics: [],
                attachs: null,
                metadata: null,
                isAnonymous: false,
                isDraft: false,
                isBestWanswer: false
            },
            validation: {
                description: null,
            },

            expanded: false,
            formCycle: RequestLifeCycle.None,
            lastError: null,
            maxHeight: this.minHeight,

            richEditor: false,
            forceRichEditor: false,

            selectedAttach: null,
            uploadingStatus: "none",
        };
    }

    ///
    private getMaxEditorHeight() {
        var sd = HtmlUtils.screenDimension();
        return Math.max(300, sd.height - 400);
    }

    ///
    private updateRecord(data: Partial<GetAnswerDto>) {
        return { ...this.state.record, ...data };
    }

    ///
    private analyse(text: string) {
        const attach = this.state.record.metadata;

        searchLink(text)
            .then((metadata) => {
                if (metadata == null && attach != null)
                    this.setState({
                        record: this.updateRecord({ metadata: null, attachs: null }),
                        selectedAttach: null
                    });
                else if (attach == null && metadata != null) {
                    this.setState({
                        selectedAttach: AttachType.Link,
                        record: this.updateRecord({ metadata, attachs: null })
                    });
                }
            });
    }

    ///
    private validateDescription(text: string, html: string, files: FileThumbnaildGetDto[]) {
        return (html != null && text != null && (text.length > 0 || html.indexOf("<img") > -1 || html.indexOf("<video") > 1)
            || (files != null && files.length > 0));
    }

    ///
    private forceRicheEditor(html: string) {
        let forceRichEditor = false;
        const tags = ["<a ", "<b>", "<b ", "<i", "<s", "<strong", "<em", "<img", "<ol", "<ul", "<li", "<font", "<blockquote", "<pre"];
        for (var i = 0; i < tags.length && !forceRichEditor; i++) forceRichEditor = html.indexOf(tags[i]) > -1;
        return forceRichEditor;
    }

    ///
    private handleEditorChange = (content: string, delta: any, source: any, editor: any) => {
        const text = editor.getText().trim();

        this.setState({
            record: this.updateRecord({ contentBody: content, contentText: text }),
            forceRichEditor: this.forceRicheEditor(content),
            validation: { ...this.state.validation, description: this.validateDescription(text, content, this.state.record.attachs) }
        });

        if (this.state.selectedAttach == null || this.state.selectedAttach == AttachType.Link) {
            this.analyse(text);
        }
    };


    ///
    private handleFileUploaded(e: AsyncUploadModel) {
        var data: FileThumbnaildGetDto = ConvertUploadToFileThumbnaildGetDto(e);
        var at = MediaUtils.getAttachTypeFromMimeType(data.mainFile.mimeType);

        if (at == AttachType.Image) {
            const files = this.state.record.attachs == null ? [] : this.state.record.attachs;
            files.push(data);
            this.setState({
                selectedAttach: AttachType.Image,
                record: this.updateRecord({ attachs: files, metadata: null }),
                validation: { ...this.state.validation, description: this.validateDescription(this.state.record.contentText, this.state.record.contentBody, files) }
            });
        }
        else if (at == AttachType.Video)
            this.setState({
                selectedAttach: AttachType.Video,
                record: this.updateRecord({ attachs: [data], metadata: null }),
                validation: { ...this.state.validation, description: this.validateDescription(this.state.record.contentText, this.state.record.contentBody, [data]) }
            });
        else
            this.setState({
                selectedAttach: AttachType.File,
                record: this.updateRecord({ attachs: [data], metadata: null }),
                validation: { ...this.state.validation, description: this.validateDescription(this.state.record.contentText, this.state.record.contentBody, [data]) }
            });
    }

    ///
    private handleUploadRemove(e: FileThumbnaildGetDto | ContentMetadata) {

        if ((e as FileThumbnaildGetDto).mainFile) {
            const attach = e as FileThumbnaildGetDto;
            if (attach.mainFile.mimeType.indexOf("image/") > -1) {
                const files = this.state.record.attachs == null ? [] : this.state.record.attachs.filter(p => p.mainFile.fileId != attach.mainFile.fileId);
                this.setState({
                    selectedAttach: files.length == 0 ? null : AttachType.Image,
                    record: this.updateRecord({ attachs: files.length == 0 ? null : files, metadata: null }),
                    validation: { ...this.state.validation, description: this.validateDescription(this.state.record.contentText, this.state.record.contentBody, files) }
                });
            }
            else {
                this.setState({
                    selectedAttach: null,
                    record: this.updateRecord({ attachs: null, metadata: null }),
                    validation: { ...this.state.validation, description: this.validateDescription(this.state.record.contentText, this.state.record.contentBody, this.state.record.attachs) }
                });
            }
        }
        else
            this.setState({
                selectedAttach: null,
                record: this.updateRecord({ attachs: null, metadata: null }),
                validation: { ...this.state.validation, description: this.validateDescription(this.state.record.contentText, this.state.record.contentBody, this.state.record.attachs) }
            });
    }

    ///
    private handleUploadingStatus = (status: UploadingStatus) => {
        this.setState({ uploadingStatus: status });
    };


    ///
    private load(answerId: string) {
        this.setState({ formCycle: RequestLifeCycle.Loading });

        pubApi.getAnswer(answerId)
            .then((record) => {

                var selectedAttach = null;
                if (record.metadata != null)
                    selectedAttach = AttachType.Link;
                else {
                    var attachsList = record.attachs.filter(p => !p.mainFile.isEmbed);
                    if (attachsList.length > 0) {
                        selectedAttach = MediaUtils.getAttachTypeFromMimeType(attachsList[0].mainFile.mimeType);
                    }
                }

                this.setState({
                    formCycle: RequestLifeCycle.None,
                    record,
                    selectedAttach
                });
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.LoadFailed });
            });
    }

    ///
    private canSave(): boolean {
        const { record, validation, uploadingStatus } = this.state;

        return uploadingStatus == "uploading"
            ? false
            : validation.description;
    }

    ///
    private save(isDraft: boolean) {
        const { record } = this.state;

        const dto: SaveAnswerDto = {
            questionId: record.questionId,
            publicationId: record.publicationId,
            contentBody: record.contentBody,
            isAnonymous: record.isAnonymous,
            isDraft: isDraft,
            title: null,
            topics: null,
            relativesTopics: null,
            attachs: record.attachs
                ? record.attachs.map((v) => {
                    return { fileId: v.mainFile.fileId, thumbnailId: v.thumbnail == null ? null : v.thumbnail.fileId };
                })
                : null,
            metadata: record.metadata
        };

        this.setState({ formCycle: RequestLifeCycle.Posting });

        pubApi.saveAnswer(dto)
            .then((result) => {
                this.props.onClose(result);
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });
    }


    ///
    private renderEditor() {
        const { classes, questionTitle } = this.props;
        const { record, maxHeight, richEditor, forceRichEditor, validation, selectedAttach } = this.state;
        const cls = classNames(classes.container, classes.containerFlex);

        const acceptFileTypes = [AttachType.File, AttachType.Image, AttachType.Link];
        if (Session.hasRole(SystemRoles.PublisherVideo)) acceptFileTypes.push(AttachType.Video);

        return (
            <>
                <DialogContent className={classes.dialogContent}>

                    <div className={cls} id={this.qlEditorId}>

                        <GridEx container xs direction="column">
                            <GridEx item >
                                <MuiContainer margin="dense">
                                    <MuiWell>
                                        {questionTitle || record.questionTitle}
                                    </MuiWell>
                                </MuiContainer>
                                <MuiContainer margin="normal">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="isAnonymous"
                                                checked={record.isAnonymous}
                                                onChange={
                                                    (e) => {
                                                        this.setState({
                                                            record: this.updateRecord({ isAnonymous: (e.target as HTMLInputElement).checked })
                                                        });
                                                    }}
                                                value="isAnonymous"
                                                color="secondary"
                                            />
                                        }
                                        label={this.props.t<string>("Views.Publication.Common.Form.AnonymousText")}
                                    />
                                </MuiContainer>
                            </GridEx>

                            <GridEx item container xs>
                                <div
                                    id={this.qlEditorId}
                                    className={classNames(
                                        classes.containerData_textarea,
                                        { ["" + classes.fieldError]: validation.description === false })
                                    }
                                >
                                    <QuillEditor
                                        autoFocus
                                        id={this.qlEditorId}
                                        minHeight={richEditor || forceRichEditor ? maxHeight : this.minHeight}
                                        showToolbar={richEditor || forceRichEditor}
                                        content={record.contentBody}
                                        placeholder={this.props.t("Views.Publication.CreateAnswer.EditorPlaceholder")}
                                        onRef={(ref) => this.editorRef = ref}
                                        onChange={this.handleEditorChange}
                                    />
                                </div>

                                <Grid container justifyContent="center" alignItems="flex-start" spacing={2}>
                                    {
                                        !forceRichEditor &&
                                        <Grid item>
                                            <MuiRegularButton
                                                simple
                                                title={this.props.t("Views.Publication.Common.Form.RichEditorTooltip")}
                                                color={richEditor ? "warning" : "primary"}
                                                onClick={() => this.setState({ richEditor: !richEditor }, () => this.editorRef.focus())}
                                            >
                                                <span><FontAwesomeIcon icon={faMarker} /> </span>
                                            </MuiRegularButton>
                                        </Grid>
                                    }
                                    <Grid item xs>
                                        <HandleAttach
                                            publicationId={record.publicationId}
                                            acceptedTypes={acceptFileTypes}
                                            selectedType={selectedAttach}
                                            attach={selectedAttach == AttachType.Link ? record.metadata : record.attachs}
                                            onUploadComplete={(e) => this.handleFileUploaded(e)}
                                            onUploadRemove={(e) => this.handleUploadRemove(e)}
                                            onUploadStatusChange={this.handleUploadingStatus}
                                        />
                                    </Grid>
                                </Grid>

                            </GridEx>
                        </GridEx>

                    </div>

                </DialogContent>

                <DialogActions>
                    {
                        (record.publicationId == null || record.isDraft) &&
                        <MuiRegularButton
                            variant="contained"
                            color="warning"
                            onClick={() => this.save(true)}
                            disabled={!this.canSave()}
                        >
                            <span>{this.props.t("Views.Publication.Common.Form.ButtonDraftText")}</span>
                        </MuiRegularButton>
                    }

                    <MuiRegularButton
                        variant="contained"
                        color="success"
                        style={{ marginLeft: "auto" }}
                        onClick={() => this.save(false)}
                        disabled={!this.canSave()}
                    >
                        <span>{this.props.t("Views.Publication.Common.Form.ButtonFinishText")}</span>
                    </MuiRegularButton>
                </DialogActions>
            </>
        );
    }

    ///
    private renderError() {
        return (
            <>
                <DialogContent className={this.props.classes.dialogContent}>
                    <RequestErrorReport error={this.state.lastError} />
                </DialogContent>
                <DialogActions />
            </>
        );
    }

    ///
    private checkForUpdateState() {
        var h = this.getMaxEditorHeight();
        if (h != this.state.maxHeight) this.setState({ maxHeight: h });
    }

    ///
    public componentDidUpdate() {
        this.checkForUpdateState();
    }

    ///
    public componentDidMount() {
        if (this.props.answerId != null) {
            this.load(this.props.answerId);
        }
    }

    ///
    public render() {
        const { lastError, formCycle, expanded } = this.state;

        return (
            <Dialog
                open={true}
                fullScreen={expanded}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography variant="button">
                                <span>{this.props.t("Views.Publication.CreateAnswer.Title")}</span>
                            </Typography>
                        </Grid>
                        <Grid item>
                            <IconButtonExpandScreen
                                color="inherit"
                                size="small"
                                expanded={expanded}
                                onClick={() => this.setState({ expanded: !expanded })}
                            />
                            <IconButtonClose
                                color="inherit"
                                size="small"
                                onClick={() => this.props.onClose(null)}
                            />
                        </Grid>
                    </Grid>
                </DialogTitle>

                {formCycle == RequestLifeCycle.Loading && <RequestIndicator type="request" />}
                {formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                {
                    lastError && formCycle != RequestLifeCycle.PostFailed
                        ? this.renderError()
                        : this.renderEditor()
                }
            </Dialog>
        );
    }
}

export default withTranslation()(withStyles(styles)(withSnackbar(CreateAnswer)));